import { api } from "@/lib/apiClient";
import { timesheetStatusLabel } from "@/pages/stundennachweise/timesheetFilterLogic";

export type HoursPipelineStage =
  | "ohne_nachweis"
  | "draft"
  | "ready"
  | "signed"
  | "invoice_draft"
  | "invoice_created"
  | "invoice_paid";

export type HoursPipelineStageMap = Record<HoursPipelineStage, number>;

export interface HoursPipelineSummary extends HoursPipelineStageMap {
  not_invoiced_hours: number;
  total_hours: number;
}

export interface HoursPipelineProjectRow {
  auftrag_id: number | null;
  auftragsnummer: string | null;
  beschreibung: string | null;
  kunde: string | null;
  stages: HoursPipelineStageMap;
}

export interface HoursPipelinePersonRow {
  person_name: string;
  stages: HoursPipelineStageMap;
}

export interface HoursPipelineReport {
  period: { start: string; end: string };
  summary: HoursPipelineSummary;
  by_project: HoursPipelineProjectRow[];
  by_person: HoursPipelinePersonRow[];
}

export const HOURS_PIPELINE_STAGES: HoursPipelineStage[] = [
  "ohne_nachweis",
  "draft",
  "ready",
  "signed",
  "invoice_draft",
  "invoice_created",
  "invoice_paid",
];

export const HOURS_PIPELINE_STAGE_LABELS: Record<HoursPipelineStage, string> = {
  ohne_nachweis: "Ohne Nachweis",
  draft: timesheetStatusLabel("draft"),
  ready: timesheetStatusLabel("ready"),
  signed: timesheetStatusLabel("signed"),
  invoice_draft: timesheetStatusLabel("invoice_draft"),
  invoice_created: timesheetStatusLabel("invoice_created"),
  invoice_paid: timesheetStatusLabel("invoice_paid"),
};

export const HOURS_PIPELINE_STAGE_DESCRIPTIONS: Record<HoursPipelineStage, string> = {
  ohne_nachweis: "Hero-Zeiten ohne Stundennachweis-Zuordnung",
  draft: "Stunden in Stundennachweis-Entwürfen",
  ready: "Fertiggestellte Stundennachweise (noch nicht unterschrieben)",
  signed: "Unterschriebene Stundennachweise",
  invoice_draft: "Stunden in Rechnungsentwürfen",
  invoice_created: "Abgerechnete Stunden (Rechnung erstellt)",
  invoice_paid: "Bezahlte Stunden",
};

export function emptyHoursPipelineStageMap(): HoursPipelineStageMap {
  return {
    ohne_nachweis: 0,
    draft: 0,
    ready: 0,
    signed: 0,
    invoice_draft: 0,
    invoice_created: 0,
    invoice_paid: 0,
  };
}

export function formatPipelineHours(hours: number | null | undefined): string {
  if (!Number.isFinite(hours as number)) return "–";
  return `${(hours as number).toFixed(2).replace(".", ",")} h`;
}

export function sumPipelineStages(stages: HoursPipelineStageMap): number {
  return HOURS_PIPELINE_STAGES.reduce((sum, stage) => sum + (stages[stage] ?? 0), 0);
}

export function formatProjectLabel(row: HoursPipelineProjectRow): string {
  const parts = [row.auftragsnummer, row.beschreibung].filter(Boolean);
  if (parts.length) return parts.join(" – ");
  return "Unbekannt";
}

export type HoursPipelineSortDirection = "asc" | "desc";

export function comparePipelineProjectRows(
  a: HoursPipelineProjectRow,
  b: HoursPipelineProjectRow,
  key: string,
): number {
  if (key === "project") {
    const labelA = [formatProjectLabel(a), a.kunde].filter(Boolean).join(" ");
    const labelB = [formatProjectLabel(b), b.kunde].filter(Boolean).join(" ");
    return labelA.localeCompare(labelB, "de", { sensitivity: "base" });
  }
  if (key === "total") {
    return sumPipelineStages(a.stages) - sumPipelineStages(b.stages);
  }
  if ((HOURS_PIPELINE_STAGES as readonly string[]).includes(key)) {
    const stage = key as HoursPipelineStage;
    return (a.stages[stage] ?? 0) - (b.stages[stage] ?? 0);
  }
  return 0;
}

export function comparePipelinePersonRows(
  a: HoursPipelinePersonRow,
  b: HoursPipelinePersonRow,
  key: string,
): number {
  if (key === "person") {
    return String(a.person_name || "").localeCompare(String(b.person_name || ""), "de", {
      sensitivity: "base",
    });
  }
  if (key === "total") {
    return sumPipelineStages(a.stages) - sumPipelineStages(b.stages);
  }
  if ((HOURS_PIPELINE_STAGES as readonly string[]).includes(key)) {
    const stage = key as HoursPipelineStage;
    return (a.stages[stage] ?? 0) - (b.stages[stage] ?? 0);
  }
  return 0;
}

export function sortPipelineRows<T>(
  rows: T[],
  key: string,
  direction: HoursPipelineSortDirection,
  compare: (a: T, b: T, key: string) => number,
): T[] {
  const factor = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => compare(a, b, key) * factor);
}

export async function fetchHoursPipelineReport(
  from: string,
  to: string,
  options?: { hideFullLicense?: boolean },
): Promise<HoursPipelineReport> {
  const params = new URLSearchParams({ start: from, end: to });
  if (options?.hideFullLicense === false) {
    params.set("hide_full_license", "false");
  }
  return api.get<HoursPipelineReport>(`/reports/hours-pipeline?${params}`);
}
